import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../shared/sheet-animations.css';
import { useScrollLock } from '../../../_utils/useScrollLock';
import { useHistory } from 'react-router-dom';
import { Card } from '../components/Card';
import DateRangePicker from '../components/DateRangePicker';
import { SearchIcon, FilterIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon, CheckIcon } from '../icons/FinanceIcons';
import { getDatePresetLabel } from '../data/mockData';
import { financeService } from '../../../_services/finance.service';
import { receiptService } from '../../../_services';
import useWindowSize from '../../new-service/useWindowSize';
import { siteColour } from '../../../_services/sites.service';
import { REFUNDS_VISIBLE } from '../../../config/featureFlags';
import RefundPanel from '../../refunds/RefundPanel';
import RefundBadge from '../../refunds/RefundBadge';
import { refundService } from '../../../_services/refund.service';
import { isRefundEntry, refundEntrySaleId, refundStatusOf, canOfferRefund, refundRowView, money, minusMoney } from '../../refunds/refundLogic';

// A by-hand line starts from the price after any code (= menu when there's none).
const fromPrice = (l) => Math.round(((Number(l.charged) || 0) + (Number(l.off) || 0) / Math.max(1, Number(l.qty) || 1)) * 100) / 100;
const FUNDING_LABEL = { salon: 'salon pays', stylist: 'stylist pays', shared: 'shared' };
// A discount code that still counts on this sale (not rejected or given back), or null.
const discountFromPromo = (p) => (p && p.code && Number(p.amount) > 0 && !p.rejected && !p.releasedAt
  ? { code: p.code, amount: Number(p.amount), funding: FUNDING_LABEL[p.fundingMode] || null, online: p.channel === 'online' }
  : null);


// Helper to format date as YYYY-MM-DD
const formatDateForApi = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to get date range from preset
const getDateRangeFromPreset = (preset) => {
  const now = new Date();
  let startDate, endDate;

  switch (preset) {
    case 'today':
      startDate = endDate = new Date(now);
      break;
    case 'yesterday':
      startDate = endDate = new Date(new Date().setDate(new Date().getDate() - 1));
      break;
    case 'last7':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - 7));
      break;
    case 'last4weeks':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - 28));
      break;
    case 'last6months':
      endDate = new Date();
      startDate = new Date(new Date().setMonth(new Date().getMonth() - 6));
      break;
    case 'last12months':
      endDate = new Date();
      startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
      break;
    case 'wtd':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - new Date().getDay()));
      break;
    case 'mtd':
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      break;
    case 'qtd':
      endDate = new Date();
      const quarter = Math.floor(new Date().getMonth() / 3);
      startDate = new Date(new Date().getFullYear(), quarter * 3, 1);
      break;
    case 'ytd':
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), 0, 1);
      break;
    default:
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  }

  return { startDate, endDate };
};

// Transaction Detail Panel
const TransactionDetail = ({ transaction, onClose, onRefunded }) => {
  const { width: detailWidth } = useWindowSize();
  const isDetailMobile = detailWidth <= 768;
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Refunds (beu-node-v2 docs/refunds.md): the panel, a reload after one is
  // made, and the sale's refund rows when the detail doesn't carry them.
  const [refundOpen, setRefundOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [refundRows, setRefundRows] = useState(null);

  // Send receipt state
  const [sendReceiptOpen, setSendReceiptOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [receiptSending, setReceiptSending] = useState(false);
  const [receiptEmailError, setReceiptEmailError] = useState('');
  const [receiptSent, setReceiptSent] = useState(false);
  const [receiptError, setReceiptError] = useState('');

  // Shared ref-counted scroll lock while the detail panel is open. The old
  // inline version also cleared overflow unconditionally on every close,
  // which could unlock a different overlay's lock.
  useScrollLock(!!transaction);

  useEffect(() => {
    if (transaction) {
      // Reset send receipt state when transaction changes
      setSendReceiptOpen(false);
      setReceiptEmail(transaction.clientEmail || '');
      setReceiptSending(false);
      setReceiptEmailError('');
      setReceiptSent(false);
      setReceiptError('');
      setRefundOpen(false);
    }
  }, [transaction]);

  useEffect(() => {
    if (!transaction) return;

    const fetchDetail = async () => {
      setLoadingDetail(true);
      try {
        const salonId = localStorage.getItem('salonId');
        // A sister site's sale (All locations): its own site rides as siteId,
        // salonId stays the active site (the stale-device guard pins it).
        const siteId = transaction.salonId && String(transaction.salonId) !== String(salonId) ? String(transaction.salonId) : undefined;
        const response = await financeService.getTransactionById(transaction.id, { salonId, ...(siteId ? { siteId } : {}) });
        const d = response?.data?.data;
        if (d) {
          const detailEmail = d.clientEmail || transaction.clientEmail || '';
          // Update receipt email if we got a better one from the API
          if (detailEmail && !receiptEmail) {
            setReceiptEmail(detailEmail);
          }
          setDetail({
            id: d._id || d.id || transaction.id,
            client: d.client || d.clientName || transaction.client || 'Walk-in',
            clientEmail: detailEmail,
            bookingDate: d.bookingDate || d.booking_date || '',
            transactionDate: d.transactionDate || d.transaction_date || '',
            transactionTime: d.transactionTime || d.transaction_time || '',
            stylist: d.stylist || d.staffName || d.staff || transaction.staff || '',
            method: (['noshow', 'cancelled'].includes(String(d.status || transaction.status || '').toLowerCase()) && !(parseFloat(d.charged) > 0)) ? 'None' : (d.method || d.paymentMethod || transaction.paymentMethod || ''),
            service: d.service || d.serviceName || (Array.isArray(d.services) && d.services.length > 0 ? (d.services[0].serviceName || d.services[0].name) : null),
            servicePrice: d.servicePrice ?? d.service_price ?? null,
            product: d.product || d.productName || (Array.isArray(d.products) && d.products.length > 0 ? (d.products[0].productName || d.products[0].name) : null),
            productCost: d.productCost ?? d.product_cost ?? null,
            voucher: d.voucher || d.voucherName || null,
            voucherValue: d.voucherValue ?? d.voucher_value ?? null,
            chargesForService: d.chargesForService ?? d.charges_for_service ?? null,
            deposit: d.deposit ?? null,
            tips: d.tips ?? d.tip ?? null,
            tax: d.tax || null,   // tax snapshot (docs/tax-money-path.md §3); null when untaxed
            // Discounts: the code that stuck and anything taken off by hand (beu-node-v2 discount/).
            codeDiscount: discountFromPromo(d.promo),
            manualDiscount: d.manualDiscount && Number(d.manualDiscount.amount) > 0 ? d.manualDiscount : null,
            toSalon: d.toSalon ?? d.to_salon ?? null,
            toStylist: d.toStylist ?? d.to_stylist ?? null,
            feePaidBy: d.feePaidBy || d.fee_paid_by || null,
            fee: d.fee ?? null,
            total: d.total ?? d.amount ?? transaction.amount ?? 0,
            outstanding: d.outstanding ?? 0,
            bookingTotal: d.bookingTotal ?? d.total ?? d.amount ?? 0,
            isDepositOnly: !!d.isDepositOnly,
            isOnlineSale: !!d.isOnlineSale,
            transactionRows: d.transactionRows || [],
            status: d.status || transaction.status || 'completed',
            // No-show / cancelled: what was actually taken (fee + kept deposit), from the API.
            charged: d.charged ?? null,
            chargedLateFee: d.chargedLateFee ?? 0,
            chargedRetainedDeposit: d.chargedRetainedDeposit ?? 0,
            // Refunds: booking.refundStatus / refundedTotal / refunds[] (absent = none).
            refundStatus: d.refundStatus || transaction.refundStatus || null,
            refundedTotal: d.refundedTotal ?? transaction.refundedTotal ?? 0,
            refunds: Array.isArray(d.refunds) ? d.refunds : null,
            // The sale after refunds (reporting: total minus the part off takings).
            netTotal: d.netTotal ?? null,
          });
          // No refund rows on the detail but the sale has refunds: ask the refunds API.
          if (!Array.isArray(d.refunds) && refundStatusOf({ refundStatus: d.refundStatus, refundedTotal: d.refundedTotal, total: d.total }) !== 'none') {
            refundService.list(d._id || d.id || transaction.id)
              .then((r) => setRefundRows(Array.isArray(r) ? r : ((r && r.refunds) || [])))
              .catch(() => setRefundRows(null));
          }
        } else {
          // Fallback to basic transaction data
          setDetail(null);
        }
      } catch (err) {
        console.error('Failed to fetch transaction detail:', err);
        setDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    };

    setRefundRows(null);
    fetchDetail();
  }, [transaction, reloadTick]);

  if (!transaction) return null;

  // Use detail if loaded, otherwise fall back to basic transaction data
  const t = detail || transaction;
  const isLateCharge = ['noshow', 'cancelled'].includes(String(t.status || '').toLowerCase());
  const isCompletedSale = ['completed', 'complete'].includes(String(t.status || '').toLowerCase());
  const refundedTotal = Math.abs(parseFloat(t.refundedTotal) || 0);
  const refundViews = (Array.isArray(t.refunds) ? t.refunds : (refundRows || [])).map(refundRowView).filter(Boolean);
  const showRefundAction = REFUNDS_VISIBLE && !loadingDetail && !!detail && isCompletedSale && !isLateCharge && canOfferRefund(t, { completed: true });

  const formatCurrency = (val) => val != null ? `£${Number(val).toFixed(2)}` : 'None';

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=650,height=900');
    if (!printWindow) return;

    const totalAmount = t.total != null ? Number(t.total) : (t.amount != null ? Number(t.amount) : 0);
    const subtotal = (t.servicePrice != null ? Number(t.servicePrice) : 0) + (t.productCost != null ? Number(t.productCost) : 0) + (t.voucherValue != null ? Number(t.voucherValue) : 0);
    const tipAmount = t.tips != null ? Number(t.tips) : 0;
    const savedTotal = ((t.codeDiscount && t.codeDiscount.amount) || 0) + ((t.manualDiscount && Number(t.manualDiscount.amount)) || 0);
    const depositAmount = t.deposit != null ? Number(t.deposit) : 0;
    const salonName = localStorage.getItem('salonName') || '';

    // Build services rows
    let servicesHtml = '';
    if (t.service && t.servicePrice != null && Number(t.servicePrice) > 0) {
      servicesHtml += `<tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748;">
          <span style="display: inline-block; width: 24px; height: 24px; background-color: #007EA0; color: #ffffff; border-radius: 50%; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#9986;</span>
          ${t.service}
        </td>
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748; text-align: right; font-weight: 500;">
          ${formatCurrency(t.servicePrice)}
        </td>
      </tr>`;
    }

    // Build products rows
    let productsHtml = '';
    if (t.product && t.productCost != null && Number(t.productCost) > 0) {
      productsHtml += `<tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748;">
          <span style="display: inline-block; width: 24px; height: 24px; background-color: #007EA0; color: #ffffff; border-radius: 50%; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#128722;</span>
          ${t.product}
        </td>
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748; text-align: right; font-weight: 500;">
          ${formatCurrency(t.productCost)}
        </td>
      </tr>`;
    }

    // Build vouchers rows
    let vouchersHtml = '';
    if (t.voucher && t.voucherValue != null && Number(t.voucherValue) > 0) {
      vouchersHtml += `<tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748;">
          <span style="display: inline-block; width: 24px; height: 24px; background-color: #007EA0; color: #ffffff; border-radius: 50%; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#127873;</span>
          ${t.voucher}
        </td>
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748; text-align: right; font-weight: 500;">
          ${formatCurrency(t.voucherValue)}
        </td>
      </tr>`;
    }

    // Build payments rows
    let paymentsHtml = '';
    if (depositAmount > 0) {
      paymentsHtml += `<tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748;">
          <span style="display: inline-block; width: 24px; height: 24px; background-color: #007EA0; color: #ffffff; border-radius: 50%; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#128176;</span>
          Deposit
        </td>
        <td style="padding: 14px 20px; font-size: 15px; color: #2d3748; text-align: right; font-weight: 500;">
          ${formatCurrency(depositAmount)}
        </td>
      </tr>`;
    }
    if (t.method) {
      const remainingPayment = totalAmount - depositAmount;
      if (remainingPayment > 0) {
        paymentsHtml += `<tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 14px 20px; font-size: 15px; color: #2d3748;">
            <span style="display: inline-block; width: 24px; height: 24px; background-color: #007EA0; color: #ffffff; border-radius: 50%; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#128179;</span>
            ${t.method}
          </td>
          <td style="padding: 14px 20px; font-size: 15px; color: #2d3748; text-align: right; font-weight: 500;">
            ${formatCurrency(remainingPayment)}
          </td>
        </tr>`;
      }
    }

    const clientName = t.client || 'Customer';
    const displayDate = t.transactionDate || t.bookingDate || t.date || '';

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Your Receipt</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f4f7fa; margin: 0; padding: 0; }
  .email-container { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
  @media print {
    body { background-color: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .email-container { box-shadow: none; }
    .header-section, .total-card, .item-icon, .summary-total, .info-box, .footer-section { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style></head><body>
<div style="padding: 20px 0;">
  <div class="email-container" style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 2px 12px rgba(0,0,0,0.08); border-radius: 8px; overflow: hidden;">

    <!-- Header -->
    <div class="header-section" style="position: relative; height: 180px; overflow: hidden; background: #007EA0;">
      <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 126, 160, 0.95); display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="text-align: center; color: #ffffff;">
          ${salonName ? '<p style="font-size: 18px; margin: 0 0 10px 0; opacity: 0.95; font-weight: 500;">' + salonName + '</p>' : ''}
          <h1 style="font-size: 32px; font-weight: 700; margin: 0 0 10px 0;">&#129534; Your Receipt</h1>
          <p style="font-size: 16px; margin: 0; opacity: 0.95;">Thank you for your visit!</p>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div style="padding: 40px 30px;">

      <!-- Greeting -->
      <p style="font-size: 18px; color: #2d3748; margin: 0 0 20px 0; font-weight: 600;">
        Hello ${clientName !== 'Walk-in' && clientName !== 'No Client' ? clientName : ''},
      </p>
      <p style="font-size: 16px; color: #4a5568; line-height: 1.6; margin: 0 0 30px 0;">
        Thank you for visiting ${salonName ? '<span style="font-weight: 600; color: #007EA0;">' + salonName + '</span>' : 'us'}. Here\'s your receipt for your records.
      </p>

      <!-- Total Card -->
      <div class="total-card" style="background: #007EA0; border-radius: 16px; padding: 30px; margin: 0 0 30px 0; text-align: center;">
        <p style="color: rgba(255, 255, 255, 0.9); font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 15px 0; font-weight: 600;">TOTAL PAID</p>
        <div style="color: #ffffff; font-size: 42px; font-weight: 700; margin: 0;">${formatCurrency(totalAmount)}</div>
      </div>

      <!-- Receipt Details -->
      <table cellspacing="0" cellpadding="0" border="0" width="100%" style="margin: 0 0 30px 0; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);">
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 16px 20px; font-size: 15px; color: #718096; font-weight: 600; width: 40%;">Invoice</td>
          <td style="padding: 16px 20px; font-size: 15px; color: #2d3748; font-weight: 500;">${t.id || 'None'}</td>
        </tr>
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 16px 20px; font-size: 15px; color: #718096; font-weight: 600;">Date</td>
          <td style="padding: 16px 20px; font-size: 15px; color: #2d3748; font-weight: 500;">${displayDate}</td>
        </tr>
        ${(t.stylist || t.staff) ? '<tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 16px 20px; font-size: 15px; color: #718096; font-weight: 600;">Staff Member</td><td style="padding: 16px 20px; font-size: 15px; color: #2d3748; font-weight: 500;">' + (t.stylist || t.staff) + '</td></tr>' : ''}
      </table>

      <!-- Services Section -->
      ${servicesHtml ? `
      <div style="margin: 0 0 30px 0;">
        <p style="font-size: 16px; font-weight: 600; color: #2d3748; margin: 0 0 15px 0;">&#9986;&#65039; Services</p>
        <table cellspacing="0" cellpadding="0" border="0" width="100%" style="border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); background-color: #ffffff;">
          ${servicesHtml}
        </table>
      </div>` : ''}

      <!-- Products Section -->
      ${productsHtml ? `
      <div style="margin: 0 0 30px 0;">
        <p style="font-size: 16px; font-weight: 600; color: #2d3748; margin: 0 0 15px 0;">&#128717;&#65039; Products</p>
        <table cellspacing="0" cellpadding="0" border="0" width="100%" style="border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); background-color: #ffffff;">
          ${productsHtml}
        </table>
      </div>` : ''}

      <!-- Vouchers Section -->
      ${vouchersHtml ? `
      <div style="margin: 0 0 30px 0;">
        <p style="font-size: 16px; font-weight: 600; color: #2d3748; margin: 0 0 15px 0;">&#127873; Vouchers</p>
        <table cellspacing="0" cellpadding="0" border="0" width="100%" style="border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); background-color: #ffffff;">
          ${vouchersHtml}
        </table>
      </div>` : ''}

      <!-- Summary Section -->
      <div style="margin: 0 0 30px 0;">
        <p style="font-size: 16px; font-weight: 600; color: #2d3748; margin: 0 0 15px 0;">&#128202; Summary</p>
        <table cellspacing="0" cellpadding="0" border="0" width="100%" style="border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); background-color: #ffffff;">
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 14px 20px; font-size: 15px; color: #718096;">Subtotal</td>
            <td style="padding: 14px 20px; font-size: 15px; color: #2d3748; text-align: right; font-weight: 500;">${formatCurrency(subtotal)}</td>
          </tr>
          ${tipAmount > 0 ? `<tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 14px 20px; font-size: 15px; color: #718096;">Tip</td>
            <td style="padding: 14px 20px; font-size: 15px; color: #2d3748; text-align: right; font-weight: 500;">${formatCurrency(tipAmount)}</td>
          </tr>` : ''}
          <tr class="summary-total" style="background-color: #f7fafc;">
            <td style="padding: 16px 20px; font-size: 16px; color: #2d3748; font-weight: 700;">Total</td>
            <td style="padding: 16px 20px; font-size: 16px; color: #007EA0; text-align: right; font-weight: 700;">${formatCurrency(totalAmount)}</td>
          </tr>
        </table>
      </div>

      ${savedTotal > 0 ? `<p style="font-size: 14px; color: #4a5568; margin: 0 0 24px 0;">You saved <strong>${formatCurrency(savedTotal)}</strong>${t.codeDiscount ? ` with code ${t.codeDiscount.code}` : ''}. Prices above are after the discount.</p>` : ''}

      <!-- Payments Section -->
      ${paymentsHtml ? `
      <div style="margin: 0 0 30px 0;">
        <p style="font-size: 16px; font-weight: 600; color: #2d3748; margin: 0 0 15px 0;">&#128179; Payments</p>
        <table cellspacing="0" cellpadding="0" border="0" width="100%" style="border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); background-color: #ffffff;">
          ${paymentsHtml}
          <tr style="background-color: #f7fafc;">
            <td style="padding: 16px 20px; font-size: 16px; color: #2d3748; font-weight: 700;">Total Paid</td>
            <td style="padding: 16px 20px; font-size: 16px; color: #007EA0; text-align: right; font-weight: 700;">${formatCurrency(totalAmount)}</td>
          </tr>
        </table>
      </div>` : ''}

      <!-- Thank You Box -->
      <div class="info-box" style="background-color: #f7fafc; border-left: 4px solid #007EA0; padding: 20px; margin: 30px 0; border-radius: 8px;">
        <p style="margin: 0; font-size: 15px; color: #4a5568; line-height: 1.6;">
          <strong>&#128157; Thank you for your business!</strong><br>
          We hope you enjoyed your visit${salonName ? ' to <span style="font-weight: 600; color: #007EA0;">' + salonName + '</span>' : ''}. We look forward to seeing you again soon!
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer-section" style="background-color: #f7fafc; padding: 30px; text-align: center; border-top: 1px solid #e2e8f0;">
      ${salonName ? '<p style="font-size: 13px; color: #718096; margin: 0 0 10px 0; line-height: 1.5;"><span style="font-weight: 600; color: #007EA0;">' + salonName + '</span></p>' : ''}
      <p style="font-size: 13px; color: #718096; margin: 0; line-height: 1.5;">This is an automated receipt. Please keep it for your records.</p>
    </div>

  </div>
</div>
</body></html>`);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSendReceipt = async () => {
    setReceiptEmailError('');
    setReceiptError('');

    if (!receiptEmail.trim()) {
      setReceiptEmailError('Email is required');
      return;
    }
    if (!validateEmail(receiptEmail.trim())) {
      setReceiptEmailError('Please enter a valid email');
      return;
    }

    setReceiptSending(true);
    try {
      const response = await receiptService.sendReceiptEmail({
        bookingId: t.id,
        email: receiptEmail.trim()
      });
      if (response.data && response.data.statusCode === 1) {
        setReceiptSent(true);
        setReceiptSending(false);
        // Auto-reset back to buttons after 3 seconds
        setTimeout(() => {
          setSendReceiptOpen(false);
          setReceiptSent(false);
        }, 3000);
      } else {
        setReceiptError(response.data?.message || 'Failed to send receipt');
        setReceiptSending(false);
      }
    } catch (err) {
      setReceiptError(err.response?.data?.message || 'Failed to send receipt');
      setReceiptSending(false);
    }
  };

  return (
    <div className="sheet-anim-side" style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: isDetailMobile ? '100%' : '480px',
      backgroundColor: 'white',
      boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.12)',
      zIndex: 1001,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>Transaction Details</h2>
          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', fontFamily: 'monospace', margin: '4px 0 0 0' }}>{t.id}</p>
        </div>
        <button
          onClick={onClose}
          style={{ background: '#f3f4f6', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '8px' }}
        >
          <svg style={{ width: '20px', height: '20px', color: '#737373' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content - Scrollable */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {loadingDetail && (
          <div>
            <style>{`
              @keyframes skeletonShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
              .txn-skel { background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 800px 100%; animation: skeletonShimmer 1.5s ease-in-out infinite; border-radius: 6px; }
            `}</style>
            {/* Client info card skeleton */}
            <div style={{ backgroundColor: '#1a1a1a', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#404040' }} />
                <div style={{ flex: 1 }}>
                  <div className="txn-skel" style={{ width: 120, height: 16, marginBottom: 8, opacity: 0.3 }} />
                  <div className="txn-skel" style={{ width: 160, height: 12, opacity: 0.2 }} />
                </div>
              </div>
            </div>
            {/* Amount skeleton */}
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '20px', marginBottom: '20px', textAlign: 'center' }}>
              <div className="txn-skel" style={{ width: 80, height: 12, margin: '0 auto 12px' }} />
              <div className="txn-skel" style={{ width: 140, height: 28, margin: '0 auto 8px' }} />
              <div className="txn-skel" style={{ width: 60, height: 20, margin: '0 auto', borderRadius: 10 }} />
            </div>
            {/* Details rows skeleton */}
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div className="txn-skel" style={{ width: 80, height: 14 }} />
                <div className="txn-skel" style={{ width: 120, height: 14 }} />
              </div>
            ))}
          </div>
        )}

        {!loadingDetail && (
          <>
            {/* Client Info Card */}
            <div style={{ backgroundColor: '#1a1a1a', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#404040', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg style={{ width: '24px', height: '24px', color: '#9ca3af' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: 'white', margin: '0 0 2px 0' }}>{t.client || 'No Client'}</p>
                  {(t.clientEmail) && <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{t.clientEmail}</p>}
                </div>
              </div>
            </div>

            {/* Booking Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Booking Information</h3>
              <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px', margin: '0 0 4px 0' }}>Booking Date</p>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.bookingDate || t.date || 'None'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px', margin: '0 0 4px 0' }}>Transaction Date</p>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.transactionDate || t.date || 'None'} {t.transactionTime || t.time || ''}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px', margin: '0 0 4px 0' }}>Stylist</p>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.stylist || t.staff || 'None'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px', margin: '0 0 4px 0' }}>Payment Method</p>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.method || t.paymentMethod || 'None'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Items (Services, Products, Vouchers, Tips) */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Items</h3>
              <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', overflow: 'hidden' }}>
                {(t.service || (t.services && t.services.length > 0)) && (
                  <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e5e5' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.service || (Array.isArray(t.services) ? t.services.join(', ') : '')}</p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>Service</p>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.servicePrice != null ? formatCurrency(t.servicePrice) : 'None'}</p>
                  </div>
                )}
                {(t.product || (t.products && t.products.length > 0)) && (
                  <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e5e5' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.product || (Array.isArray(t.products) ? t.products.join(', ') : '')}</p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>Product</p>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.productCost != null ? formatCurrency(t.productCost) : 'None'}</p>
                  </div>
                )}
                {t.voucher && (
                  <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e5e5' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.voucher}</p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>Voucher</p>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{t.voucherValue != null ? formatCurrency(t.voucherValue) : 'None'}</p>
                  </div>
                )}
                {t.tips != null && t.tips > 0 && (
                  <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>Tips</p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>Gratuity</p>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#059669', margin: 0 }}>{formatCurrency(t.tips)}</p>
                  </div>
                )}
                {/* Show a fallback if no items exist (from basic transaction data) */}
                {!t.service && !(t.services && t.services.length > 0) && !t.product && !(t.products && t.products.length > 0) && !t.voucher && !(t.tips > 0) && (
                  <div style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>No item details available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Discounts: why the prices above are below the menu */}
            {(t.codeDiscount || t.manualDiscount) && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Discounts</h3>
                <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '4px 16px' }}>
                  {t.codeDiscount && (
                    <div style={{ padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>Code {t.codeDiscount.code}</p>
                        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>{[t.codeDiscount.online ? 'Used when booking online' : 'Used at checkout', t.codeDiscount.funding].filter(Boolean).join(' · ')}</p>
                      </div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0, whiteSpace: 'nowrap' }}>{minusMoney(t.codeDiscount.amount)}</p>
                    </div>
                  )}
                  {t.manualDiscount && (
                    <div style={{ padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', borderTop: t.codeDiscount ? '1px solid #e5e5e5' : 'none' }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>Taken off by hand{t.manualDiscount.byName ? ` · ${t.manualDiscount.byName}` : ''}</p>
                        {(t.manualDiscount.lines || []).map((l, i) => (
                          <p key={i} style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>{l.name || (l.kind === 'product' ? 'Product' : 'Service')}{l.qty > 1 ? ` × ${l.qty}` : ''}: {formatCurrency(fromPrice(l))} → {formatCurrency(l.charged)}</p>
                        ))}
                        {t.manualDiscount.hadRight === false && (
                          <p style={{ fontSize: '12px', color: '#b45309', margin: '2px 0 0 0' }}>{t.manualDiscount.overrode ? 'Their role doesn’t include Discounts · card already taken' : 'Their role doesn’t include Discounts'}</p>
                        )}
                      </div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0, whiteSpace: 'nowrap' }}>{minusMoney(t.manualDiscount.amount)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* No-show / cancelled: show what was charged, not the booking's price split */}
            {isLateCharge ? (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Charge</h3>
                <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {parseFloat(t.chargedLateFee) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', color: '#737373' }}>{(t.status || '').toLowerCase() === 'noshow' ? 'No-show fee' : 'Cancellation fee'} · card on file</span>
                      <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{formatCurrency(t.chargedLateFee)}</span>
                    </div>
                  )}
                  {parseFloat(t.chargedRetainedDeposit) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', color: '#737373' }}>Deposit kept</span>
                      <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{formatCurrency(t.chargedRetainedDeposit)}</span>
                    </div>
                  )}
                  {!(parseFloat(t.charged) > 0) && (
                    <span style={{ fontSize: '13px', color: '#737373' }}>Nothing was charged for this booking.</span>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e5e5', paddingTop: '10px' }}>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>Booked value</span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{formatCurrency(t.bookingTotal)}</span>
                  </div>
                </div>
              </div>
            ) : (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Financial Breakdown</h3>
              <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Items */}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#737373' }}>Service Price</span>
                    <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{formatCurrency(t.servicePrice)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#737373' }}>Product Cost</span>
                    <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{formatCurrency(t.productCost)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#737373' }}>Voucher Value</span>
                    <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{formatCurrency(t.voucherValue)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#737373' }}>Tips</span>
                    <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{formatCurrency(t.tips)}</span>
                  </div>
                  {/* Tax inside the sale, one row per rate, from the booking's snapshot.
                      Stored prices are gross in both modes, so it always reads "Includes". */}
                  {(() => {
                    const tax = t.tax;
                    // Any line that carried a rate counts — a zero-rated sale shows "VAT 0%".
                    if (!tax || !(tax.lines || []).some((l) => (l.taxes || []).length)) return null;
                    const byRate = {};
                    (tax.lines || []).forEach((l) => (l.taxes || []).forEach((x) => {
                      const k = `${x.name} ${x.rate}%`; byRate[k] = (byRate[k] || 0) + (Number(x.amount) || 0);
                    }));
                    return [
                      ...Object.keys(byRate).map((k) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '13px', color: '#737373' }}>Includes {k}</span>
                          <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{formatCurrency(byRate[k])}</span>
                        </div>
                      )),
                      tax.taxNumber ? (
                        <div key="taxno" style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>Tax no.</span>
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>{tax.taxNumber}</span>
                        </div>
                      ) : null
                    ];
                  })()}

                  {/* Per-transaction blocks: deposit, final, splits all rendered as their own block */}
                  {(() => {
                    const rows = Array.isArray(t.transactionRows) ? t.transactionRows : [];
                    if (rows.length === 0) return null;
                    const PAYMENT_LABELS = { 1: 'Online', 2: 'Card', 3: 'Cash', 4: 'Other', 5: 'Beu Terminal', 10: 'Card on File', 11: 'Voucher', 12: 'Payment Link', 13: 'Klarna', 14: 'Clearpay' };
                    const totalAmount = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0) || 1;
                    const totalFee = parseFloat(t.fee) || 0;
                    const totalSalon = parseFloat(t.toSalon) || 0;
                    const totalStylist = parseFloat(t.toStylist) || 0;
                    return rows.map((r, i) => {
                      const share = (parseFloat(r.amount) || 0) / totalAmount;
                      const rowFee = totalFee * share;
                      const rowSalon = totalSalon * share;
                      const rowStylist = totalStylist * share;
                      const label = (r.paymentType === 12 && t.isOnlineSale) ? 'Online Store' : (PAYMENT_LABELS[r.paymentType] || 'Payment');
                      const isDeposit = !!r.isDeposit || r.pgObjectType === 'deposit';
                      const ts = r.createdAt ? new Date(r.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
                      return (
                        <div key={i} style={{ borderTop: '1px solid #e5e5e5', paddingTop: '12px', marginTop: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                              <span style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {isDeposit ? 'Deposit' : (rows.length > 1 ? `Payment ${i + 1}` : 'Payment')}
                              </span>
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>{label}{ts ? ` · ${ts}` : ''}</span>
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>{formatCurrency(r.amount)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                            <span style={{ fontSize: '12px', color: '#737373' }}>To Salon</span>
                            <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{formatCurrency(rowSalon)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                            <span style={{ fontSize: '12px', color: '#737373' }}>To Stylist</span>
                            <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{formatCurrency(rowStylist)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                            <span style={{ fontSize: '12px', color: '#737373' }}>Fee</span>
                            <span style={{ fontSize: '12px', color: '#ef4444' }}>{formatCurrency(rowFee)}</span>
                          </div>
                        </div>
                      );
                    });
                  })()}

                  {/* Rolled-up totals (always show, useful when there's only one row too) */}
                  <div style={{ borderTop: '1px solid #d4d4d8', paddingTop: '12px', marginTop: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', color: '#737373', fontWeight: '500' }}>Total To Salon</span>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(t.toSalon)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                      <span style={{ fontSize: '13px', color: '#737373', fontWeight: '500' }}>Total To Stylist</span>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(t.toStylist)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                      <span style={{ fontSize: '13px', color: '#737373', fontWeight: '500' }}>Total Fee{t.feePaidBy ? ` (${t.feePaidBy} pays)` : ''}</span>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#ef4444' }}>{formatCurrency(t.fee)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Outstanding (deposit-only-paid bookings) */}
            {t.isDepositOnly && parseFloat(t.outstanding) > 0 && (
              <div style={{ marginBottom: '12px', padding: '12px 16px', backgroundColor: '#fef3c7', borderRadius: '10px', border: '1px solid #f59e0b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#92400e' }}>Outstanding · pending checkout</span>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#92400e' }}>{formatCurrency(t.outstanding)}</span>
              </div>
            )}

            {/* Total */}
            <div style={{ backgroundColor: '#007EA0', borderRadius: '10px', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', fontWeight: '500', color: 'white' }}>{isLateCharge ? 'Charged' : t.isDepositOnly ? 'Captured' : 'Total'}</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: 'white' }}>{formatCurrency(isLateCharge ? (t.charged || 0) : (t.total != null ? t.total : t.amount))}</span>
            </div>

            {/* Refunded so far (spec: the sale keeps its own period; refunds are dated when made) */}
            {refundedTotal > 0 && !isLateCharge && (
              <div style={{ marginTop: '10px', padding: '14px 20px', backgroundColor: '#fafafa', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: '#737373' }}>Refunded</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#b42318' }}>{minusMoney(refundedTotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: '#737373' }}>Kept</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>{money(t.netTotal != null ? t.netTotal : Math.max(0, (parseFloat(t.total != null ? t.total : t.amount) || 0) - refundedTotal))}</span>
                </div>
              </div>
            )}

            {/* Refund lines: when, how much, how, who, why, and the shares taken back */}
            {refundViews.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Refunds</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {refundViews.map((r) => (
                    <div key={r.id} style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' }}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>{r.method}{r.status === 'pending' ? ' · pending' : r.status === 'failed' ? ' · failed' : ''}</p>
                          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>
                            {r.at ? new Date(r.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            {r.by ? ` · by ${r.by}` : (r.source === 'stripe' ? ' · made in Stripe' : '')}
                          </p>
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: r.status === 'failed' ? '#9ca3af' : '#b42318', whiteSpace: 'nowrap', textDecoration: r.status === 'failed' ? 'line-through' : 'none' }}>{minusMoney(r.amount)}</span>
                      </div>
                      {(r.what || r.reason || r.note || r.reversals.length > 0 || r.fee) && (
                        <div style={{ borderTop: '1px solid #ececec', marginTop: '10px', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {r.what && <p style={{ fontSize: '12px', color: '#4b5563', margin: 0 }}>{r.what}{r.restocked ? ' · back in stock' : ''}</p>}
                          {r.reason && <p style={{ fontSize: '12px', color: '#4b5563', margin: 0 }}>{r.reason}{r.note ? `: ${r.note}` : ''}</p>}
                          {!r.reason && r.note && <p style={{ fontSize: '12px', color: '#4b5563', margin: 0 }}>{r.note}</p>}
                          {r.reversals.map((v, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '12px', color: '#737373' }}>Taken back from {v.role === 'salon' ? 'the salon' : (v.name || 'the stylist')}</span>
                              <span style={{ fontSize: '12px', color: '#737373' }}>{money(v.amount)}</span>
                            </div>
                          ))}
                          {r.fee && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '12px', color: '#737373' }}>Card fee kept, covered by {r.fee.name || 'the seller'}</span>
                              <span style={{ fontSize: '12px', color: '#737373' }}>{money(r.fee.amount)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status */}
            <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#d1fae5', borderRadius: '10px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#059669', margin: 0 }}>
                {(t.status || '').toLowerCase() === 'completed' || (t.status || '').toLowerCase() === 'complete' ? '✓ ' : ''}{t.status || 'Complete'}
              </p>
              <RefundBadge sale={t} />
            </div>
          </>
        )}
      </div>

      {/* Footer Actions */}
      <div style={{ borderTop: '1px solid #e5e5e5', backgroundColor: 'white' }}>
        {!sendReceiptOpen ? (
          /* Default: Print + Send Receipt buttons */
          <div style={{ padding: '16px 24px', display: 'flex', gap: '12px' }}>
            <button
              onClick={handlePrint}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: 'white',
                color: '#1a1a1a',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            {showRefundAction && (
              <button
                onClick={() => setRefundOpen(true)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: 'white',
                  color: '#1a1a1a',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 010 11H11" />
                </svg>
                Refund
              </button>
            )}
            <button
              onClick={() => setSendReceiptOpen(true)}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#007EA0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send Receipt
            </button>
          </div>
        ) : receiptSent ? (
          /* Success state */
          <div style={{ padding: '20px 24px', textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px'
            }}>
              <svg style={{ width: '20px', height: '20px', color: '#059669' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#059669', margin: 0 }}>Receipt sent to {receiptEmail}</p>
          </div>
        ) : (
          /* Send receipt inline form */
          <div style={{ padding: '16px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>Send Receipt</p>
              <button
                onClick={() => { setSendReceiptOpen(false); setReceiptEmailError(''); setReceiptError(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', fontSize: '12px' }}
              >
                Cancel
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="email"
                  value={receiptEmail}
                  onChange={(e) => { setReceiptEmail(e.target.value); setReceiptEmailError(''); setReceiptError(''); }}
                  placeholder="Enter email address"
                  disabled={receiptSending}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendReceipt(); } }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${receiptEmailError ? '#ef4444' : '#e5e5e5'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1a1a1a',
                    backgroundColor: receiptSending ? '#f9fafb' : 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <button
                onClick={handleSendReceipt}
                disabled={receiptSending}
                style={{
                  padding: '10px 20px',
                  backgroundColor: receiptSending ? '#5eafc2' : '#007EA0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: receiptSending ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {receiptSending ? (
                  <>
                    <div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Sending
                  </>
                ) : (
                  <>
                    <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Send
                  </>
                )}
              </button>
            </div>
            {receiptEmailError && (
              <p style={{ fontSize: '12px', color: '#ef4444', margin: '6px 0 0 0' }}>{receiptEmailError}</p>
            )}
            {receiptError && (
              <p style={{ fontSize: '12px', color: '#ef4444', margin: '6px 0 0 0' }}>{receiptError}</p>
            )}
          </div>
        )}
      </div>
      {refundOpen && (
        <RefundPanel
          bookingId={t.id}
          clientName={t.client && t.client !== 'No Client' ? t.client : null}
          clientEmail={t.clientEmail || receiptEmail}
          onClose={() => setRefundOpen(false)}
          onRefunded={() => { setReloadTick((n) => n + 1); if (onRefunded) onRefunded(); }}
        />
      )}
    </div>
  );
};

// Transactions View Component
const TransactionsView = ({ selectedTransaction, setSelectedTransaction, initialFilter = 'all', staffFilter = 'all', initialTransactionId = null, salonIds = null, selectedSites = [] }) => {
  // The Location column only appears when the header filter spans sites.
  const multiSite = !!(salonIds && selectedSites.length > 1);
  const { width } = useWindowSize();
  const history = useHistory();

  // Deep link: /transactions/:id opens that transaction's panel directly.
  // A minimal {id} is enough — the panel fetches full detail by id itself.
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (initialTransactionId && !deepLinkApplied.current) {
      deepLinkApplied.current = true;
      setSelectedTransaction({ id: initialTransactionId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTransactionId]);

  // Keep the URL addressable: selecting a row puts its id in the path,
  // closing the panel returns to the bare list.
  const selectTransaction = (t) => {
    setSelectedTransaction(t);
    if (t && t.id) history.replace(`/transactions/${t.id}`);
  };
  const closeTransaction = () => {
    setSelectedTransaction(null);
    history.replace('/transactions');
  };
  const isMobile = width <= 768;
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(initialFilter !== 'all' ? [initialFilter] : []);
  const [statusFilter, setStatusFilter] = useState([]);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState([]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePreset, setDatePreset] = useState('mtd');
  const [dateStart, setDateStart] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [dateEnd, setDateEnd] = useState(new Date());
  const [filtersOpen, setFiltersOpen] = useState(false);

  // API data states
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [refundEntries, setRefundEntries] = useState([]);
  const [refundTotalsInRange, setRefundTotalsInRange] = useState(null);

  // Use ref for cursor to avoid recreating fetchTransactions on cursor change
  const cursorRef = useRef(null);

  // Get salonId from localStorage
  const salonId = localStorage.getItem('salonId');

  // Fetch transactions from API
  const fetchTransactions = useCallback(async (loadMore = false) => {
    if (!salonId) {
      setError('No salon selected');
      setLoading(false);
      return;
    }

    if (!loadMore) {
      setLoading(true);
      cursorRef.current = null; // Reset cursor when doing fresh fetch
    }
    setError(null);

    const { startDate, endDate } = datePreset === null
      ? { startDate: dateStart, endDate: dateEnd }
      : getDateRangeFromPreset(datePreset);
    const params = {
      salonId,
      ...(salonIds ? { salonIds } : {}),
      startDate: formatDateForApi(startDate),
      endDate: formatDateForApi(endDate),
      type: typeFilter.length > 0 ? typeFilter.join(',') : undefined,
      status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
      paymentMethod: paymentMethodFilter.length > 0 ? paymentMethodFilter.join(',') : undefined,
      search: searchQuery || undefined,
      hairdresserId: staffFilter !== 'all' ? staffFilter : undefined,
      limit: 50,
      cursor: loadMore ? cursorRef.current : undefined
    };

    console.log('[TransactionsView] fetchTransactions called with params:', JSON.stringify(params));

    try {
      const response = await financeService.getTransactions(params);
      const data = response?.data?.data;

      if (data) {
        const mapRow = (t, i) => {
          // A refund entry (negative, dated when made) opens the sale it belongs to.
          const refundEntry = isRefundEntry(t);
          const saleId = refundEntry ? (refundEntrySaleId(t) || t._id || t.id) : (t._id || t.id);
          return {
          id: saleId,
          rowKey: refundEntry ? `refund-${t.refundId || t._id || t.id || i}-${saleId}` : (t._id || t.id),
          isRefund: refundEntry,
          refundStatus: refundEntry ? null : (t.refundStatus || null),
          refundedTotal: refundEntry ? 0 : (t.refundedTotal || 0),
          // Newest first across sales and refund entries (YYYY-MM-DD HH:mm).
          sortKey: `${String(t.date || t.createdAt || '').slice(0, 10)} ${t.time || ''}`,
          date: t.date
            ? new Date(t.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : new Date(t.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
          time: t.time || new Date(t.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
          client: t.client || t.clientName || 'Walk-in',
          clientEmail: t.clientEmail || '',
          // Which location the sale is from — shown when the filter spans sites.
          salonId: t.salonId || null,
          salonName: t.salonName || '',
          staff: t.staff || t.staffName || 'Unknown',
          type: refundEntry ? 'Refund' : (t.type || 'Service'),
          services: Array.isArray(t.services) ? t.services.map(s => s.serviceName || s.name || 'Unknown Service') : [],
          products: Array.isArray(t.products) ? t.products.map(p => p.productName || p.name || 'Unknown Product') : [],
          amount: refundEntry ? -Math.abs(t.amount || 0) : (t.amount || 0),
          tip: refundEntry ? 0 : (t.tip || 0),
          discount: refundEntry ? 0 : (Number(t.discount) || 0),
          paymentMethod: t.paymentMethod || 'Card',
          status: refundEntry ? 'refund' : (t.status || 'completed')
          };
        };
        const newTransactions = Array.isArray(data.transactions) ? data.transactions.map(mapRow) : [];

        if (loadMore) {
          setTransactions(prev => [...prev, ...newTransactions]);
        } else {
          setTransactions(newTransactions);
          // Refunds made in the range come on the first page only, as their
          // own list (refundRows) with a { count, amount } total. Absent on
          // older servers: nothing extra shows.
          setRefundEntries(Array.isArray(data.refundRows) ? data.refundRows.map((t, i) => mapRow({ ...t, isRefund: true }, i)) : []);
          setRefundTotalsInRange(data.refundTotals && data.refundTotals.count > 0 ? data.refundTotals : null);
        }

        setHasMore(data.hasMore || false);
        cursorRef.current = data.nextCursor || null;
      }
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [salonId, salonIds, datePreset, dateStart, dateEnd, typeFilter, statusFilter, paymentMethodFilter, searchQuery, staffFilter]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    console.log('[TransactionsView] useEffect triggered - staffFilter:', staffFilter, 'salonId:', salonId);
    fetchTransactions();
  }, [salonId, salonIds, datePreset, dateStart, dateEnd, typeFilter, statusFilter, paymentMethodFilter, staffFilter, fetchTransactions]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== undefined) {
        fetchTransactions();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update filter when initialFilter changes
  useEffect(() => {
    if (initialFilter !== 'all') {
      setTypeFilter([initialFilter]);
    }
  }, [initialFilter]);

  const typeOptions = [
    { id: 'all', label: 'All Types' },
    { id: 'service', label: 'Services' },
    { id: 'product', label: 'Products' },
    { id: 'voucher', label: 'Voucher' },
    { id: 'commission', label: 'Commission' },
  ];

  const statusOptions = [
    { id: 'all', label: 'All Status' },
    { id: 'booked', label: 'Booked' },
    { id: 'completed', label: 'Complete' },
    { id: 'noshow', label: 'No Show' },
    { id: 'cancelled', label: 'Cancelled' },
  ];

  const paymentMethodOptions = [
    { id: 'all', label: 'All Payments' },
    { id: 1, label: 'Online' },
    { id: 3, label: 'Cash' },
    { id: 5, label: 'Beu Card Readers' },
    { id: 2, label: 'Other Card Readers' },
    { id: 4, label: 'Other' },
    { id: 10, label: 'Saved Card' },
    { id: 11, label: 'Vouchers' },
    { id: 12, label: 'Quick Pay (Link & QR)' },
    { id: 13, label: 'Klarna' },
    { id: 14, label: 'Clearpay' },
  ];

  // Toggle filter helper
  const toggleFilter = (filterArray, setFilter, value) => {
    if (filterArray.includes(value)) {
      setFilter(filterArray.filter(v => v !== value));
    } else {
      setFilter([...filterArray, value]);
    }
  };

  // Count active filters
  const activeFilterCount = typeFilter.length + statusFilter.length + paymentMethodFilter.length;

  // Build active filters list for chips
  const activeFilters = [];
  typeFilter.forEach(type => {
    activeFilters.push({
      key: `type-${type}`,
      label: typeOptions.find(t => t.id === type)?.label,
      onRemove: () => setTypeFilter(typeFilter.filter(t => t !== type))
    });
  });
  statusFilter.forEach(status => {
    activeFilters.push({
      key: `status-${status}`,
      label: statusOptions.find(s => s.id === status)?.label,
      onRemove: () => setStatusFilter(statusFilter.filter(s => s !== status))
    });
  });
  paymentMethodFilter.forEach(payment => {
    activeFilters.push({
      key: `payment-${payment}`,
      label: paymentMethodOptions.find(p => p.id === payment)?.label,
      onRemove: () => setPaymentMethodFilter(paymentMethodFilter.filter(p => p !== payment))
    });
  });

  // Client-side filtering for immediate feedback (search only - status and payment filters handled by API)
  // Refund entries interleave with the sales by when they happened.
  const listed = refundEntries.length
    ? [...transactions, ...refundEntries].sort((a, b) => (a.sortKey < b.sortKey ? 1 : a.sortKey > b.sortKey ? -1 : 0))
    : transactions;
  const filteredTransactions = listed.filter(t => {
    const matchesSearch = searchQuery === '' ||
      t.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.id.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });
  
  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState(null);
  const [exportCountLoading, setExportCountLoading] = useState(false);

  // Fetch export count only when user hovers over export button
  const fetchExportCount = async () => {
    if (!salonId || exportCountLoading) return;

    setExportCountLoading(true);
    try {
      const { startDate, endDate } = datePreset === null
        ? { startDate: dateStart, endDate: dateEnd }
        : getDateRangeFromPreset(datePreset);
      const params = {
        salonId,
        ...(salonIds ? { salonIds } : {}),
      ...(salonIds ? { salonIds } : {}),
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate)
      };
      if (typeFilter.length > 0) params.type = typeFilter.join(',');
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      if (paymentMethodFilter.length > 0) params.paymentMethod = paymentMethodFilter.join(',');
      if (staffFilter && staffFilter !== 'all') params.hairdresserId = staffFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await financeService.getExportCount(params);
      setExportCount(response?.data?.data || null);
    } catch (err) {
      console.error('Failed to fetch export count:', err);
      setExportCount(null);
    } finally {
      setExportCountLoading(false);
    }
  };

  // Export transactions as CSV (streamed from backend)
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { startDate, endDate } = datePreset === null
        ? { startDate: dateStart, endDate: dateEnd }
        : getDateRangeFromPreset(datePreset);
      const params = {
        salonId,
        ...(salonIds ? { salonIds } : {}),
      ...(salonIds ? { salonIds } : {}),
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate)
      };
      if (typeFilter.length > 0) params.type = typeFilter.join(',');
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      if (paymentMethodFilter.length > 0) params.paymentMethod = paymentMethodFilter.join(',');
      if (staffFilter && staffFilter !== 'all') params.hairdresserId = staffFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await financeService.exportTransactionsCSV(params);

      // Create blob download link
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = formatDateForApi(startDate) + '_to_' + formatDateForApi(endDate);
      link.download = `transactions_${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export transactions:', err);

      // Handle specific error types from backend
      const errorData = err.response?.data;
      if (errorData?.error === 'DATE_RANGE_TOO_LARGE') {
        alert(`Export Error: ${errorData.message}\n\nRequested: ${errorData.requestedDays} days\nMaximum: ${errorData.maxDays} days (1 year)\n\nTip: Export by year or quarter instead.`);
      } else if (errorData?.error === 'EXPORT_TOO_LARGE') {
        alert(`Export Limit Exceeded\n\nYour selection contains ${errorData.count?.toLocaleString()} transactions.\nExport limit: ${errorData.limit?.toLocaleString()} transactions.\n\nPlease:\n• Narrow your date range\n• Apply filters (staff or transaction type)\n• Export multiple smaller date ranges`);
      } else if (errorData?.error === 'EXPORT_IN_PROGRESS') {
        alert(`Export Already Running\n\nYou already have an export in progress.\nElapsed time: ${errorData.elapsedSeconds}s\n\nPlease wait for it to complete before starting another.`);
      } else if (errorData?.error === 'SYSTEM_BUSY') {
        alert(`System Busy\n\nThe export system is currently at capacity (${errorData.systemLoad}).\nYour export: ${errorData.yourExportSize}\n\nPlease wait ${errorData.estimatedWaitSeconds}s and try again.`);
      } else {
        alert('Failed to export transactions. Please try a smaller date range or try again later.');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'center' : 'flex-start' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>Transactions</h2>
          <p style={{ fontSize: '14px', color: '#737373' }}>
            View and manage all payment transactions
            {refundTotalsInRange && (
              <span style={{ color: '#9ca3af' }}> · {refundTotalsInRange.count} {refundTotalsInRange.count === 1 ? 'refund' : 'refunds'}, {minusMoney(refundTotalsInRange.amount)}</span>
            )}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
          <button
            onClick={handleExport}
            onMouseEnter={fetchExportCount}
            disabled={exporting || exportCount?.exceedsLimit}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: exporting || exportCount?.exceedsLimit ? '#f9fafb' : 'white',
              border: exportCount?.exceedsLimit ? '1px solid #ef4444' : '1px solid #e5e5e5',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '500',
              color: exporting || exportCount?.exceedsLimit ? '#9ca3af' : '#1a1a1a',
              cursor: exporting || exportCount?.exceedsLimit ? 'not-allowed' : 'pointer',
              opacity: exporting || exportCount?.exceedsLimit ? 0.7 : 1,
              whiteSpace: 'nowrap'
            }}
          >
            {exporting ? (
              <svg style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83" />
              </svg>
            ) : (
              <DownloadIcon size={16} />
            )}
            {exporting ? 'Exporting...' : 'Export'}
          </button>
          {!isMobile && exportCount && !exportCountLoading && (
            <small style={{
              fontSize: '12px',
              color: exportCount.exceedsLimit ? '#ef4444' : exportCount.count > 15000 ? '#f59e0b' : '#737373'
            }}>
              {exportCount.exceedsLimit ? (
                <>⚠️ {exportCount.count.toLocaleString()} rows (exceeds 25k limit)</>
              ) : exportCount.count > 15000 ? (
                <>⚠️ ~{exportCount.count.toLocaleString()} rows (~{exportCount.estimatedTimeSeconds}s)</>
              ) : (
                <>~{exportCount.count.toLocaleString()} rows (~{exportCount.estimatedTimeSeconds}s)</>
              )}
              {exportCount.systemLoad > 80 && !exportCount.exceedsLimit && (
                <> • System {exportCount.systemLoad}% busy</>
              )}
            </small>
          )}
          {!isMobile && exportCountLoading && (
            <small style={{ fontSize: '12px', color: '#9ca3af' }}>
              Calculating...
            </small>
          )}
        </div>
      </div>
      
      {/* Filter Bar */}
      <div style={{
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '12px',
        padding: '12px 16px',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e5e5'
      }}>
        {/* Date + Filters row on mobile */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Date Range Picker */}
          <div style={{ position: 'relative', flex: isMobile ? 1 : 'none' }}>
            <button
              onClick={() => setDatePickerOpen(!datePickerOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 14px',
                backgroundColor: '#fafafa',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#737373',
                cursor: 'pointer',
                transition: 'all 0.15s',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              <svg style={{ width: '16px', height: '16px', color: '#737373', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span style={{ color: '#007EA0', fontWeight: '500', whiteSpace: 'nowrap' }}>
                {datePreset === null && dateStart && dateEnd
                  ? `${dateStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${dateEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                  : getDatePresetLabel(datePreset)
                }
              </span>
              <svg style={{ width: '14px', height: '14px', color: '#9ca3af', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <DateRangePicker
              isOpen={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              startDate={dateStart}
              endDate={dateEnd}
              selectedPreset={datePreset}
              onPresetChange={setDatePreset}
              onApply={(start, end, preset) => {
                setDateStart(start);
                setDateEnd(end);
                if (preset === undefined) {
                  setDatePreset(null);
                }
              }}
            />
          </div>

          {/* Filters Button - inline with date on mobile */}
          {isMobile && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  backgroundColor: activeFilterCount > 0 ? '#f0f9ff' : '#fafafa',
                  border: activeFilterCount > 0 ? '1px solid #007EA0' : '1px solid #e5e5e5',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: activeFilterCount > 0 ? '#007EA0' : '#4b5563',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <FilterIcon size={16} />
                {activeFilterCount > 0 && (
                  <span style={{
                    backgroundColor: '#007EA0',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '2px 7px',
                    borderRadius: '10px',
                    minWidth: '18px',
                    textAlign: 'center'
                  }}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div style={{ position: 'relative', flex: 1, maxWidth: isMobile ? 'none' : '400px' }}>
          <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}><SearchIcon size={16} /></span>
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 40px 10px 42px',
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: '#fafafa',
              outline: 'none',
              transition: 'all 0.15s',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#007EA0'; e.target.style.backgroundColor = 'white'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.backgroundColor = '#fafafa'; }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
            >
              <svg style={{ width: '16px', height: '16px', color: '#9ca3af' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Spacer - desktop only */}
        {!isMobile && <div style={{ flex: 1 }} />}

        {/* Filters Button - desktop only */}
        {!isMobile && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                backgroundColor: activeFilterCount > 0 ? '#f0f9ff' : '#fafafa',
                border: activeFilterCount > 0 ? '1px solid #007EA0' : '1px solid #e5e5e5',
                borderRadius: '8px',
                fontSize: '14px',
                color: activeFilterCount > 0 ? '#007EA0' : '#4b5563',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              <FilterIcon size={16} />
              <span style={{ fontWeight: '500' }}>Filters</span>
              {activeFilterCount > 0 && (
                <span style={{
                  backgroundColor: '#007EA0',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '2px 7px',
                  borderRadius: '10px',
                  minWidth: '18px',
                  textAlign: 'center'
                }}>
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Filters Dropdown Panel - Desktop */}
            {filtersOpen && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                  onClick={() => setFiltersOpen(false)}
                />
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #e5e5e5',
                  zIndex: 100,
                  width: '360px',
                  padding: '16px'
                }}>
                  {/* Type Filter */}
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Type</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {typeOptions.filter(o => o.id !== 'all').map((option) => (
                        <button
                          key={option.id}
                          onClick={() => toggleFilter(typeFilter, setTypeFilter, option.id)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            border: typeFilter.includes(option.id) ? '1px solid #007EA0' : '1px solid #e5e5e5',
                            backgroundColor: typeFilter.includes(option.id) ? '#f0f9ff' : 'white',
                            color: typeFilter.includes(option.id) ? '#007EA0' : '#4b5563',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Status</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {statusOptions.filter(o => o.id !== 'all').map((option) => (
                        <button
                          key={option.id}
                          onClick={() => toggleFilter(statusFilter, setStatusFilter, option.id)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            border: statusFilter.includes(option.id) ? '1px solid #007EA0' : '1px solid #e5e5e5',
                            backgroundColor: statusFilter.includes(option.id) ? '#f0f9ff' : 'white',
                            color: statusFilter.includes(option.id) ? '#007EA0' : '#4b5563',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Payment Method Filter */}
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Payment Method</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {paymentMethodOptions.filter(o => o.id !== 'all').map((option) => (
                        <button
                          key={option.id}
                          onClick={() => toggleFilter(paymentMethodFilter, setPaymentMethodFilter, option.id)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            border: paymentMethodFilter.includes(option.id) ? '1px solid #007EA0' : '1px solid #e5e5e5',
                            backgroundColor: paymentMethodFilter.includes(option.id) ? '#f0f9ff' : 'white',
                            color: paymentMethodFilter.includes(option.id) ? '#007EA0' : '#4b5563',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #e5e5e5' }}>
                    <button
                      onClick={() => { setTypeFilter([]); setStatusFilter([]); setPaymentMethodFilter([]); }}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: 'transparent',
                        color: '#737373',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Reset all
                    </button>
                    <button
                      onClick={() => setFiltersOpen(false)}
                      style={{
                        padding: '8px 20px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: '#007EA0',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filters Bottom Sheet - Mobile */}
      {isMobile && filtersOpen && (
        <>
          <div
            className="sheet-anim-backdrop"
            style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={() => setFiltersOpen(false)}
          />
          <div className="sheet-anim-up" style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 201,
            padding: '0 16px 16px',
            maxHeight: '80vh',
            overflowY: 'auto',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom))'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 -16px 16px', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>Filters</h3>
              <button
                onClick={() => setFiltersOpen(false)}
                style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px', color: '#1a1a1a' }}
              >
                &#x2715;
              </button>
            </div>

            {/* Type Filter */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Type</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {typeOptions.filter(o => o.id !== 'all').map((option) => (
                  <button
                    key={option.id}
                    onClick={() => toggleFilter(typeFilter, setTypeFilter, option.id)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: typeFilter.includes(option.id) ? 'none' : '1px solid #e5e5e5',
                      backgroundColor: typeFilter.includes(option.id) ? '#29282D' : 'white',
                      color: typeFilter.includes(option.id) ? 'white' : '#4b5563',
                      cursor: 'pointer'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Status</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {statusOptions.filter(o => o.id !== 'all').map((option) => (
                  <button
                    key={option.id}
                    onClick={() => toggleFilter(statusFilter, setStatusFilter, option.id)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: statusFilter.includes(option.id) ? 'none' : '1px solid #e5e5e5',
                      backgroundColor: statusFilter.includes(option.id) ? '#29282D' : 'white',
                      color: statusFilter.includes(option.id) ? 'white' : '#4b5563',
                      cursor: 'pointer'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Method Filter */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Payment Method</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {paymentMethodOptions.filter(o => o.id !== 'all').map((option) => (
                  <button
                    key={option.id}
                    onClick={() => toggleFilter(paymentMethodFilter, setPaymentMethodFilter, option.id)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: paymentMethodFilter.includes(option.id) ? 'none' : '1px solid #e5e5e5',
                      backgroundColor: paymentMethodFilter.includes(option.id) ? '#29282D' : 'white',
                      color: paymentMethodFilter.includes(option.id) ? 'white' : '#4b5563',
                      cursor: 'pointer'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', borderTop: '1px solid #e5e5e5' }}>
              <button
                onClick={() => { setTypeFilter([]); setStatusFilter([]); setPaymentMethodFilter([]); }}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: '1px solid #e5e5e5',
                  borderRadius: '10px',
                  backgroundColor: 'white',
                  color: '#737373',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Reset all
              </button>
              <button
                onClick={() => setFiltersOpen(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: 'none',
                  borderRadius: '10px',
                  backgroundColor: '#29282D',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

      {/* Active Filter Chips */}
      {activeFilters.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: '#737373' }}>Active:</span>
          {activeFilters.map((filter) => (
            <span
              key={filter.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                backgroundColor: '#f0f9ff',
                border: '1px solid #007EA0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#007EA0',
                fontWeight: '500'
              }}
            >
              {filter.label}
              <button
                onClick={filter.onRemove}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
              >
                <svg style={{ width: '14px', height: '14px', color: '#007EA0' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          <button
            onClick={() => { setTypeFilter([]); setStatusFilter([]); setPaymentMethodFilter([]); }}
            style={{
              padding: '6px 10px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: '#737373',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Clear all
          </button>
        </div>
      )}
      
      {/* Transactions List */}
      {isMobile ? (
        /* Mobile Card List */
        <div>
          {loading && (
            <div style={{ padding: '16px' }}>
              <style>{`
                @keyframes skeletonShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
                .txn-skel { background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 800px 100%; animation: skeletonShimmer 1.5s ease-in-out infinite; border-radius: 6px; }
              `}</style>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div>
                    <div className="txn-skel" style={{ width: 120, height: 14, marginBottom: 8 }} />
                    <div className="txn-skel" style={{ width: 180, height: 11 }} />
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="txn-skel" style={{ width: 70, height: 14, marginBottom: 8, marginLeft: 'auto' }} />
                    <div className="txn-skel" style={{ width: 50, height: 11, marginLeft: 'auto', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && !loading && (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <p style={{ fontSize: '15px', color: '#ef4444', marginBottom: '12px' }}>{error}</p>
              <button onClick={() => fetchTransactions()} style={{ padding: '8px 16px', backgroundColor: '#007EA0', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>Try again</button>
            </div>
          )}

          {!loading && !error && filteredTransactions.length === 0 && (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <p style={{ fontSize: '15px', color: '#737373' }}>No transactions found</p>
            </div>
          )}

          {!loading && !error && filteredTransactions.length > 0 && (
            <div className="forms-mobile-list">
              {filteredTransactions.map((transaction) => (
                <div
                  key={transaction.rowKey || transaction.id}
                  className="forms-mobile-card"
                  onClick={() => selectTransaction(transaction)}
                >
                  <div className="forms-mobile-card-left">
                    <div className="forms-mobile-card-info">
                      <span className="forms-mobile-card-name">{transaction.client}</span>
                      <span className="forms-mobile-card-meta">
                        {transaction.date}
                        <span className="forms-mobile-card-dot"> · </span>
                        {transaction.staff}
                        {transaction.type && transaction.type !== 'Service' && (
                          <><span className="forms-mobile-card-dot"> · </span>{transaction.type}</>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="forms-mobile-card-right" style={{ alignItems: 'flex-end', gap: '4px' }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: transaction.amount >= 0 ? '#1a1a1a' : '#dc2626'
                    }}>
                      {transaction.amount >= 0 ? '' : '-'}£{Math.abs(transaction.amount).toFixed(2)}
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500',
                      backgroundColor: transaction.status === 'refund' ? '#fee2e2' : transaction.status === 'completed' ? '#dcfce7' : transaction.status === 'cancelled' ? '#fee2e2' : transaction.status === 'noshow' ? '#fef3c7' : '#f3f4f6',
                      color: transaction.status === 'refund' ? '#b42318' : transaction.status === 'completed' ? '#166534' : transaction.status === 'cancelled' ? '#991b1b' : transaction.status === 'noshow' ? '#b45309' : '#4b5563'
                    }}>
                      {transaction.status === 'refund' ? 'Refund' : transaction.status === 'completed' ? 'Complete' : transaction.status === 'cancelled' ? 'Cancelled' : transaction.status === 'noshow' ? 'No Show' : transaction.status === 'booked' ? 'Booked' : transaction.status}
                    </span>
                    <RefundBadge sale={transaction} small />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && hasMore && (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <button onClick={() => fetchTransactions(true)} style={{ padding: '10px 24px', backgroundColor: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '14px', fontWeight: '500', color: '#007EA0', cursor: 'pointer', width: '100%' }}>
                Load more
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Desktop Table */
        <Card noPadding>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e5e5e5' }}>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client</th>
                  {multiSite && <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location</th>}
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Staff</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction, idx) => (
                  <tr
                    key={transaction.rowKey || transaction.id}
                    onClick={() => selectTransaction(transaction)}
                    style={{
                      borderBottom: idx < filteredTransactions.length - 1 ? '1px solid #f0f0f0' : 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.1s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <td style={{ padding: '16px 20px' }}>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{transaction.date}</p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>{transaction.time}</p>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{transaction.client}</p>
                    </td>
                    {multiSite && (
                      <td style={{ padding: '16px 20px' }}>
                        <p style={{ fontSize: '14px', color: '#4b5563', margin: 0, whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '999px', marginRight: '8px', verticalAlign: 'middle', background: siteColour(transaction.salonId) }} />
                          {transaction.salonName || 'None'}
                        </p>
                      </td>
                    )}
                    <td style={{ padding: '16px 20px' }}>
                      <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>{transaction.staff}</p>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: transaction.type === 'Commission' ? '#fef3c7' : '#f3f4f6',
                        color: transaction.type === 'Commission' ? '#b45309' : '#4b5563'
                      }}>
                        {transaction.type}
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>{transaction.paymentMethod}</p>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: transaction.status === 'refund' ? '#fee2e2' : transaction.status === 'completed' ? '#dcfce7' : transaction.status === 'cancelled' ? '#fee2e2' : transaction.status === 'noshow' ? '#fef3c7' : '#f3f4f6',
                        color: transaction.status === 'refund' ? '#b42318' : transaction.status === 'completed' ? '#166534' : transaction.status === 'cancelled' ? '#991b1b' : transaction.status === 'noshow' ? '#b45309' : '#4b5563'
                      }}>
                        {transaction.status === 'refund' ? 'Refund' : transaction.status === 'completed' ? 'Complete' : transaction.status === 'cancelled' ? 'Cancelled' : transaction.status === 'noshow' ? 'No Show' : transaction.status === 'booked' ? 'Booked' : transaction.status}
                      </span>
                      <RefundBadge sale={transaction} small style={{ marginLeft: '6px' }} />
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <p style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: transaction.amount >= 0 ? '#1a1a1a' : '#dc2626',
                        margin: 0
                      }}>
                        {transaction.amount >= 0 ? '' : '-'}£{Math.abs(transaction.amount).toFixed(2)}
                      </p>
                      {transaction.tip > 0 && (
                        <p style={{ fontSize: '11px', color: '#059669', margin: '2px 0 0 0' }}>+£{transaction.tip.toFixed(2)} tip</p>
                      )}
                      {transaction.discount > 0 && (
                        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0 0' }}>£{transaction.discount.toFixed(2)} off</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {loading && (
              <div>
                <style>{`
                  @keyframes skeletonShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
                  .txn-skel { background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 800px 100%; animation: skeletonShimmer 1.5s ease-in-out infinite; border-radius: 6px; }
                `}</style>
                {[1,2,3,4,5,6,7,8].map(i => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ flex: '1 1 15%' }}>
                      <div className="txn-skel" style={{ width: 80, height: 14, marginBottom: 6 }} />
                      <div className="txn-skel" style={{ width: 50, height: 11 }} />
                    </div>
                    <div style={{ flex: '1 1 15%' }}><div className="txn-skel" style={{ width: 100, height: 14 }} /></div>
                    <div style={{ flex: '1 1 12%' }}><div className="txn-skel" style={{ width: 80, height: 14 }} /></div>
                    <div style={{ flex: '1 1 12%' }}><div className="txn-skel" style={{ width: 60, height: 22, borderRadius: 4 }} /></div>
                    <div style={{ flex: '1 1 12%' }}><div className="txn-skel" style={{ width: 70, height: 14 }} /></div>
                    <div style={{ flex: '1 1 12%' }}><div className="txn-skel" style={{ width: 65, height: 22, borderRadius: 4 }} /></div>
                    <div style={{ flex: '1 1 12%', textAlign: 'right' }}><div className="txn-skel" style={{ width: 70, height: 14, marginLeft: 'auto' }} /></div>
                  </div>
                ))}
              </div>
            )}

            {error && !loading && (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <p style={{ fontSize: '15px', color: '#ef4444', marginBottom: '12px' }}>{error}</p>
                <button onClick={() => fetchTransactions()} style={{ padding: '8px 16px', backgroundColor: '#007EA0', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>Try again</button>
              </div>
            )}

            {!loading && !error && filteredTransactions.length === 0 && (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <p style={{ fontSize: '15px', color: '#737373' }}>No transactions found</p>
              </div>
            )}

            {!loading && hasMore && (
              <div style={{ padding: '16px', textAlign: 'center', borderTop: '1px solid #e5e5e5' }}>
                <button onClick={() => fetchTransactions(true)} style={{ padding: '10px 24px', backgroundColor: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '14px', fontWeight: '500', color: '#007EA0', cursor: 'pointer' }}>
                  Load more
                </button>
              </div>
            )}
          </div>
        </Card>
      )}
      
      {/* Transaction Detail Panel */}
      {selectedTransaction && (
        <>
          <div 
            className="sheet-anim-backdrop"
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1000 }}
            onClick={closeTransaction}
          />
          <TransactionDetail 
            transaction={selectedTransaction} 
            onClose={closeTransaction} 
            onRefunded={() => fetchTransactions()}
          />
        </>
      )}
    </div>
  );
};

export default TransactionsView;
